import React, { useCallback, useEffect, useRef, useState } from 'react';
import { useDispatch } from 'react-redux';
import { matchPath, Outlet, useNavigate, useLocation } from 'react-router';
import { useTranslation } from 'src/arb-wrapper';
import AppLayout from '@amzn/meridian/app-layout';
import Masthead, { MastheadTitle } from '@amzn/meridian/masthead';
import SideMenu, { SideMenuLink } from '@amzn/meridian/side-menu';
import Column from '@amzn/meridian/column';
import Text from '@amzn/meridian/text';
import Button from '@amzn/meridian/button';
import Icon from '@amzn/meridian/icon';
import menuTokens from '@amzn/meridian-tokens/base/icon/menu';
import supportChatTokens from '@amzn/meridian-tokens/base/icon/support-chat';
import Alert from '@amzn/meridian/alert';
import Sheet from '@amzn/meridian/sheet';
import { useDocumentTitle } from 'src/hooks/useDocumentTitle';
import { useAuth } from 'src/hooks/use-auth';
import {
  ChatPanelSessionRegistrationProvider,
  deriveContext,
  deriveEntity,
  useChatPanel,
} from 'src/contexts/ChatPanelContext';
import { AgentFab } from 'src/components/chat/AgentFab';
import { ChatWidget } from 'src/components/chat/ChatWidget';
import { type ResumableSession, UNASSIGNED_ENTITY_ID } from 'src/components/chat/ChatHistoryView';
import { IssueReportModal } from 'src/components/issue-report/IssueReportModal';
import { setFeatureFlags, isFeatureEnabled } from 'src/feature-flags';
import {
  leelaApi,
  useGetFeatureFlagsQuery,
  useCampaignConfigurationControllerFindManyQuery,
} from 'src/store/leela-api';
import type { AppDispatch } from 'src/store';
import type { CampaignDesiredStateDto } from 'src/store/generated/leela-api-generated';
import {
  ASSISTANT_ROUTES,
  FEATURE_FLAGS,
  CreationSource,
  ConfigurationType,
  DATA_ASSISTANT_ROUTES,
} from 'src/constants';
import { useUser } from 'src/contexts/UserContext';
import {
  canAccessInternalInsights,
  canAccessPaidMediaDashboards,
  canAccessPublisherInsights,
} from 'src/hooks/insightsAccess';
import { useAllowedScope } from 'src/hooks/useAllowedScope';
import { useCanPerform } from 'src/hooks/useCanPerform';
import { useMyPermissions } from 'src/hooks/useMyPermissions';
import { CampaignPublishers } from 'src/store/constants';
import { NoAccessPage } from 'src/routes/NoAccessPage';
import { SideSheetProvider, useSideSheet } from 'src/contexts/SideSheetContext';
import { ViewAsDialog, ViewAsFrame } from 'src/components/view-as';

const CHAT_PANEL_WIDTH_PX = 450;

const getCampaignResumePath = ({ sessionId, entityId }: ResumableSession): string | undefined =>
  entityId && entityId !== UNASSIGNED_ENTITY_ID
    ? `/campaigns/${encodeURIComponent(entityId)}?sessionId=${encodeURIComponent(sessionId)}`
    : undefined;

export const Root = () => {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const location = useLocation();
  useDocumentTitle();
  const { user, authError, signIn } = useAuth();
  const { user: currentUser } = useUser();
  const [flagsReady, setFlagsReady] = useState(false);
  const [insightsOpen, setInsightsOpen] = useState(true);
  const [adminOpen, setAdminOpen] = useState(true);
  const [menuOpen, setMenuOpen] = useState(false);
  const [isIssueReportOpen, setIsIssueReportOpen] = useState(false);
  const [isViewAsOpen, setIsViewAsOpen] = useState(false);
  const { open: chatOpen, mode: chatMode } = useChatPanel();

  useEffect(() => {
    signIn();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const { data: featureFlags, isSuccess, isError: isFeatureFlagsError } = useGetFeatureFlagsQuery(undefined, {
    skip: !user,
  });

  const { data: globalBlocklistData } = useCampaignConfigurationControllerFindManyQuery({
    configType: ConfigurationType.GTI_BLOCKLIST,
    source: CreationSource.ADMIN,
    pageSize: 1,
    includeCampaignCount: false,
  }, { skip: !user || !currentUser?.isAdmin });

  const {
    assignments,
    isBusinessUnitAdmin,
    isError,
    isLoading,
    isPlatformAdmin,
  } = useMyPermissions({ skip: !user });
  const {
    isLoading: isScopeLoading,
    publishers: allowedPublishers,
  } = useAllowedScope('VIEW_CAMPAIGNS', { skip: !user });

  useEffect(() => {
    if (isSuccess) {
      if (featureFlags) setFeatureFlags(featureFlags);
      setFlagsReady(true);
    } else if (isFeatureFlagsError) {
      setFlagsReady(true);
    }
  }, [featureFlags, isSuccess, isFeatureFlagsError]);

  if (authError) {
    return <Alert type="error">{authError}</Alert>;
  }

  if (!user || !flagsReady) {
    // eslint-disable-next-line react/jsx-no-useless-fragment
    return <></>;
  }

  // Enforcement stays flagged because unseeded permission groups would lock out all users.
  const permissionsEnforcementEnabled = isFeatureEnabled(
    FEATURE_FLAGS.PERMISSIONS_ENFORCEMENT_ENABLED,
  );
  if (permissionsEnforcementEnabled && isLoading) {
    // Unassigned users must not glimpse route content before their access is known.
    // eslint-disable-next-line react/jsx-no-useless-fragment
    return <></>;
  }
  if (
    permissionsEnforcementEnabled &&
    !isLoading &&
    !isError &&
    !isPlatformAdmin &&
    !isBusinessUnitAdmin &&
    assignments.length === 0
  ) {
    return (
      <ViewAsFrame>
        <NoAccessPage />
      </ViewAsFrame>
    );
  }

  const showBlocklist = isFeatureEnabled(FEATURE_FLAGS.ENABLE_BLOCKLIST);
  const insightsAccess = { assignments, isError, publishers: allowedPublishers };
  const hideInternalInsights =
    isLoading || !canAccessInternalInsights(insightsAccess);
  const hasPublisherAccess = (
    publisher: CampaignDesiredStateDto['publisher'],
  ): boolean =>
    !isScopeLoading && canAccessPublisherInsights(insightsAccess, publisher);
  const showReportingDashboard = isFeatureEnabled(FEATURE_FLAGS.ENABLE_REPORTING_DASHBOARD);
  const showSpotlightLal =
    isFeatureEnabled(FEATURE_FLAGS.ENABLE_SPOTLIGHT_LAL) && !hideInternalInsights;
  const showSocialSignals =
    isFeatureEnabled(FEATURE_FLAGS.ENABLE_SOCIAL_SIGNALS) && !hideInternalInsights;
  const isAdmin = currentUser?.isAdmin;
  const showPaidMediaDashboard =
    isFeatureEnabled(FEATURE_FLAGS.ENABLE_PAID_MEDIA_DASHBOARD) &&
    canAccessPaidMediaDashboards(currentUser);
  const showGoogleAdsPerformance =
    showPaidMediaDashboard &&
    isFeatureEnabled(FEATURE_FLAGS.ENABLE_GOOGLE_DEMANDGEN);
  const showEntityMetrics = isFeatureEnabled(FEATURE_FLAGS.ENABLE_ENTITY_METRICS);
  const showIssueReport = isFeatureEnabled(FEATURE_FLAGS.ENABLE_ISSUE_REPORTS);
  const showBoxFolders =
    isFeatureEnabled(FEATURE_FLAGS.ENABLE_BOX_FOLDERS) && !hideInternalInsights;
  const showAccessManagement = !!isAdmin || isBusinessUnitAdmin;
  // While viewing as someone, /me describes the target, who is never an admin, so the entry hides itself.
  const showViewAs = !!isAdmin && isFeatureEnabled(FEATURE_FLAGS.ENABLE_VIEW_AS);
  const currentPath = location.pathname;
  const globalBlocklistId = globalBlocklistData?.data?.[0]?.id;

  const handleNav = (href: string) => () => {
    navigate(href);
    setMenuOpen(false);
  };

  return (
    <ViewAsFrame>
    <SideSheetProvider>
    <AppLayout headerComponent={Masthead} sidebarComponent={SideMenu}>
      <Masthead backgroundColor="brand" size="small">
        <MastheadTitle>
          <span style={{ display: 'flex', alignItems: 'center', gap: '12px' }}>
            <Button aria-label="menu" data-testid="hamburger-menu-button" type="icon" onClick={() => { setMenuOpen(!menuOpen); }}><Icon tokens={menuTokens} /></Button>
            <a
              data-testid="masthead-title-link"
              href="/"
              style={{ display: 'inline-flex', transition: 'filter 0.2s ease' }}
              onClick={(e) => { e.preventDefault(); navigate('/'); }}
              onMouseEnter={(e) => { (e.currentTarget as HTMLElement).style.filter = 'invert(27%) sepia(96%) saturate(1200%) hue-rotate(167deg) brightness(92%) contrast(101%)'; }}
              onMouseLeave={(e) => { (e.currentTarget as HTMLElement).style.filter = 'none'; }}
            >
              <img alt="Dynamic Ads" src={require('src/assets/da-logo.svg')} style={{ height: 28 }} />
            </a>
          </span>
        </MastheadTitle>
        {showIssueReport && (
          <Button aria-label={t('issue_report_trigger')} data-testid="issue-report-trigger" type="icon" onClick={() => { setIsIssueReportOpen(true); }}><Icon tokens={supportChatTokens} /></Button>
        )}
        <Text>{user.username}@</Text>
        <div
          data-testid="user-avatar"
          style={{ width: 40, height: 40, overflow: 'hidden', borderRadius: '50%', position: 'relative', margin: '5px' }}
        >
          <img alt="badge photo" src={`https://internal-cdn.amazon.com/badgephotos.amazon.com/?uid=${user.username}`} style={{ width: '100%', height: 'auto', position: 'absolute' }} />
        </div>
      </Masthead>

      <SideMenu backgroundColor="principal" minWidth={200} open={menuOpen && !(chatOpen && chatMode === 'fullscreen')} onOpen={() => { setMenuOpen(true); }}>
        <SideMenuLink data-testid="masthead-header-campaigns" href="/campaigns" selected={currentPath.startsWith('/campaigns') || currentPath === '/'} onClick={handleNav('/campaigns')}>
          {t('campaigns')}
        </SideMenuLink>
        <SideMenuLink data-testid="masthead-header-titles" href="/titles" selected={currentPath.startsWith('/titles')} onClick={handleNav('/titles')}>
          {t('titles')}
        </SideMenuLink>
        <SideMenuLink data-testid="masthead-header-assets" href="/assets" selected={currentPath.startsWith('/assets')} onClick={handleNav('/assets')}>
          {t('assets')}
        </SideMenuLink>
        {showBoxFolders && (
          <SideMenuLink data-testid="masthead-header-box-folders" href="/box-folders" selected={currentPath.startsWith('/box-folders')} onClick={handleNav('/box-folders')}>
            {t('box_folders')}
          </SideMenuLink>
        )}
        {showBlocklist && (
          <SideMenuLink data-testid="masthead-header-blocklists" href="/blocklists" selected={currentPath.startsWith('/blocklists') && currentPath !== `/blocklists/${globalBlocklistId}`} onClick={handleNav('/blocklists')}>
            {t('blocklists')}
          </SideMenuLink>
        )}

        {showAccessManagement && (
          <SideMenuLink data-testid="masthead-header-access-management" href="/access-management" selected={currentPath.startsWith('/access-management')} onClick={handleNav('/access-management')}>
            {t('access_management_title')}
          </SideMenuLink>
        )}

        {(showReportingDashboard || showSpotlightLal || showSocialSignals || showPaidMediaDashboard) && (
        <SideMenuLink href="/reporting-dashboard" open={insightsOpen} selected={currentPath.startsWith('/reporting-dashboard') || currentPath.startsWith('/spotlight-lal') || currentPath.startsWith('/social-signals') || currentPath.startsWith('/tiktok-ads-performance') || currentPath.startsWith('/meta-ads-performance') || currentPath.startsWith('/google-ads-performance') || currentPath.startsWith('/adsp-ads-performance')} onClick={() => { setInsightsOpen(!insightsOpen); }}>
          {t('insights')}
          {showReportingDashboard && (
            <SideMenuLink data-testid="masthead-header-reporting-dashboard" href="/reporting-dashboard" selected={currentPath.startsWith('/reporting-dashboard')} onClick={handleNav('/reporting-dashboard')}>
              {t('reporting_dashboard')}
            </SideMenuLink>
          )}
          {showSpotlightLal && (
            <SideMenuLink data-testid="masthead-header-spotlight-lal" href="/spotlight-lal" selected={currentPath.startsWith('/spotlight-lal')} onClick={handleNav('/spotlight-lal')}>
              {t('spotlight_lal')}
            </SideMenuLink>
          )}
          {showSocialSignals && (
            <SideMenuLink data-testid="masthead-header-social-signals" href="/social-signals" selected={currentPath.startsWith('/social-signals')} onClick={handleNav('/social-signals')}>
              {t('social_signals')}
            </SideMenuLink>
          )}
          {showPaidMediaDashboard && (
            <SideMenuLink data-testid="masthead-header-tiktok-ads" disabled={!hasPublisherAccess(CampaignPublishers.TIKTOK)} href="/tiktok-ads-performance" selected={currentPath.startsWith('/tiktok-ads-performance')} onClick={handleNav('/tiktok-ads-performance')}>
              {t('tiktok_ads_performance')}
            </SideMenuLink>
          )}
          {showPaidMediaDashboard && (
            <SideMenuLink data-testid="masthead-header-meta-ads" disabled={!hasPublisherAccess(CampaignPublishers.META)} href="/meta-ads-performance" selected={currentPath.startsWith('/meta-ads-performance')} onClick={handleNav('/meta-ads-performance')}>
              {t('meta_ads_performance')}
            </SideMenuLink>
          )}
          {showGoogleAdsPerformance && (
            <SideMenuLink data-testid="masthead-header-google-ads" disabled={!hasPublisherAccess(CampaignPublishers.GOOGLE)} href="/google-ads-performance" selected={currentPath.startsWith('/google-ads-performance')} onClick={handleNav('/google-ads-performance')}>
              {t('google_ads_performance')}
            </SideMenuLink>
          )}
          {showPaidMediaDashboard && (
            <SideMenuLink data-testid="masthead-header-adsp-ads" disabled={!hasPublisherAccess(CampaignPublishers.ADSP)} href="/adsp-ads-performance" selected={currentPath.startsWith('/adsp-ads-performance')} onClick={handleNav('/adsp-ads-performance')}>
              {t('adsp_ads_performance')}
            </SideMenuLink>
          )}
        </SideMenuLink>
        )}

        {isAdmin && (
          <SideMenuLink href="/publisher-configs" open={adminOpen} selected={currentPath.startsWith('/publisher-configs') || (showEntityMetrics && currentPath.startsWith('/entity-metrics')) || currentPath.startsWith('/bu-management') || (!!globalBlocklistId && currentPath === `/blocklists/${globalBlocklistId}`)} onClick={() => { setAdminOpen(!adminOpen); }}>
            {t('admin')}
            {globalBlocklistId && (
              <SideMenuLink href={`/blocklists/${globalBlocklistId}`} selected={currentPath === `/blocklists/${globalBlocklistId}`} onClick={handleNav(`/blocklists/${globalBlocklistId}`)}>
                {t('global_blocklist')}
              </SideMenuLink>
            )}
            <SideMenuLink data-testid="masthead-header-publisher-configs" href="/publisher-configs" selected={currentPath.startsWith('/publisher-configs')} onClick={handleNav('/publisher-configs')}>
              {t('publisher_configs')}
            </SideMenuLink>
            {showEntityMetrics && (
              <SideMenuLink data-testid="masthead-header-entity-metrics" href="/entity-metrics" selected={currentPath.startsWith('/entity-metrics')} onClick={handleNav('/entity-metrics')}>
                {t('entity_metrics')}
              </SideMenuLink>
            )}
            <SideMenuLink data-testid="masthead-header-bu-management" href="/bu-management" selected={currentPath.startsWith('/bu-management')} onClick={handleNav('/bu-management')}>
              {t('bu_management')}
            </SideMenuLink>
            {showViewAs && (
              <SideMenuLink data-testid="masthead-header-view-as" href="#view-as" selected={false} onClick={() => { setIsViewAsOpen(true); setMenuOpen(false); }}>
                {t('view_as_user')}
              </SideMenuLink>
            )}
          </SideMenuLink>
        )}

        <SideMenuLink data-testid="masthead-header-docs" href="/user-guide" selected={currentPath.startsWith('/user-guide')} onClick={handleNav('/user-guide')}>
          {t('docs')}
        </SideMenuLink>
      </SideMenu>

   <RootSideSheet>
        <Column backgroundColor="subdued" minHeight="100%" spacingInset="500 600">
          <Outlet />
        </Column>
      </RootSideSheet>
    </AppLayout>
    {showIssueReport && isIssueReportOpen && (
      <IssueReportModal reporter={user} onClose={() => { setIsIssueReportOpen(false); }} />
    )}
    {showViewAs && (
      <ViewAsDialog open={isViewAsOpen} onClose={() => { setIsViewAsOpen(false); }} />
    )}
    </SideSheetProvider>
    </ViewAsFrame>
  );
};

const RootSideSheet: React.FC<{ children: React.ReactNode }> = ({ children }) => {
  const { isOpen, sheetContent, closeSheet } = useSideSheet();
  const { open: chatOpen, mode: chatMode, setOpen: setChatOpen, setMode: setChatMode } = useChatPanel();
  const location = useLocation();
  const navigate = useNavigate();
  const dispatch = useDispatch<AppDispatch>();
  const chatContext = deriveContext(location.pathname);
  const chatEntity = deriveEntity(location.pathname);
  const canManageCampaigns = useCanPerform('MANAGE_CAMPAIGNS');
  const { user: currentUser } = useUser();

  // Read sessionId from URL query param for returning sessions (e.g., /campaigns/123?sessionId=abc)
  const searchParams = new URLSearchParams(location.search);
  const initialSessionId = searchParams.get('sessionId') ?? undefined;
  const resolvedSessionIdRef = useRef<string | null>(null);
  const validatingSessionIdRef = useRef<string | null>(null);
  const currentSessionIdRef = useRef(initialSessionId);
  currentSessionIdRef.current = initialSessionId;
  const registerLocalSession = useCallback((sessionId: string) => {
    resolvedSessionIdRef.current = sessionId;
  }, []);
  const [hasEverOpenedChat, setHasEverOpenedChat] = useState(
    chatOpen || Boolean(initialSessionId),
  );
  const shouldRenderChat = hasEverOpenedChat || chatOpen || Boolean(initialSessionId);
  const onDataDashboard = Object.keys(DATA_ASSISTANT_ROUTES).some(
    (pattern) => matchPath(pattern, location.pathname) !== null,
  );
  const dataAssistantAvailable =
    onDataDashboard &&
    isFeatureEnabled(FEATURE_FLAGS.ENABLE_DATA_AGENT) &&
    isFeatureEnabled(FEATURE_FLAGS.ENABLE_PAID_MEDIA_DASHBOARD) &&
    currentUser?.isAdmin === true;
  const assistantAvailable =
    (ASSISTANT_ROUTES.some(
      (pattern) => matchPath(pattern, location.pathname) !== null,
    ) &&
    canManageCampaigns &&
    isFeatureEnabled(FEATURE_FLAGS.ENABLE_GOOGLE_DEMANDGEN)) ||
    dataAssistantAvailable;
  // Re-evaluated each render, so a held-back dashboard session opens once /me resolves to an admin.
  const dashboardSessionBlocked = onDataDashboard && !dataAssistantAvailable;

  useEffect(() => {
    if (chatOpen || initialSessionId) setHasEverOpenedChat(true);
  }, [chatOpen, initialSessionId]);

  useEffect(() => {
    if (dashboardSessionBlocked && chatOpen) {
      setChatOpen(false);
      setChatMode('panel');
    }
  }, [chatOpen, dashboardSessionBlocked, setChatMode, setChatOpen]);

  const navigateToSession = useCallback((sessionId: string) => {
    const params = new URLSearchParams(location.search);
    params.set('sessionId', sessionId);
    const search = params.toString();
    navigate(
      { pathname: location.pathname, search: search ? `?${search}` : '' },
      { replace: true },
    );
  }, [location.pathname, location.search, navigate]);

  const handleResume = (session: ResumableSession) => {
    registerLocalSession(session.sessionId);
    setChatOpen(true);
    const campaignResumePath = getCampaignResumePath(session);
    if (campaignResumePath) {
      navigate(campaignResumePath);
    } else {
      navigateToSession(session.sessionId);
    }
  };

  const handleNewSession = useCallback((newSessionId: string) => {
    registerLocalSession(newSessionId);
    navigateToSession(newSessionId);
  }, [navigateToSession, registerLocalSession]);

  const handleOpenAssistant = useCallback(() => {
    if (initialSessionId) {
      setChatOpen(true);
      return;
    }

    const newSessionId = crypto.randomUUID();
    registerLocalSession(newSessionId);
    setChatOpen(true);
    navigateToSession(newSessionId);
  }, [initialSessionId, navigateToSession, registerLocalSession, setChatOpen]);

  const handleCloseChat = useCallback(() => {
    setChatOpen(false);
    setChatMode('panel');
  }, [setChatMode, setChatOpen]);

  // Close chat when navigating to a different page
  const prevPathRef = useRef(location.pathname);
  useEffect(() => {
    if (prevPathRef.current !== location.pathname) {
      // Don't close chat when navigating with a sessionId for a resumed session
      if (!initialSessionId) {
        setChatOpen(false);
        setChatMode('panel');
      }
      prevPathRef.current = location.pathname;
    }
  }, [initialSessionId, location.pathname, setChatMode, setChatOpen]);

  // Auto-open chat when a new sessionId arrives in the URL
  useEffect(() => {
    if (
      !initialSessionId ||
      dashboardSessionBlocked ||
      initialSessionId === resolvedSessionIdRef.current ||
      initialSessionId === validatingSessionIdRef.current
    ) return;

    const validatedSessionId = initialSessionId;
    let cancelled = false;
    validatingSessionIdRef.current = validatedSessionId;
    const validation = dispatch(
      leelaApi.endpoints.agentSessionsControllerFindOne.initiate({
        sessionId: validatedSessionId,
      }),
    );
    const isCurrentValidation = () =>
      !cancelled && currentSessionIdRef.current === validatedSessionId;

    validation.unwrap()
      .then(() => {
        if (!isCurrentValidation()) return;

        resolvedSessionIdRef.current = validatedSessionId;
        setChatOpen(true);
      })
      .catch(() => {
        if (!isCurrentValidation()) return;

        const params = new URLSearchParams(location.search);
        params.delete('sessionId');
        const search = params.toString();
        navigate(
          { pathname: location.pathname, search: search ? `?${search}` : '' },
          { replace: true },
        );
      });

    return () => {
      cancelled = true;
      if (validatingSessionIdRef.current === validatedSessionId) {
        validatingSessionIdRef.current = null;
      }
      validation.unsubscribe();
    };
  }, [dashboardSessionBlocked, dispatch, initialSessionId, location.pathname, location.search, navigate, setChatOpen]);

  return (
    <ChatPanelSessionRegistrationProvider registerLocalSession={registerLocalSession}>
      <div className="chat-panel-host" style={{ display: 'flex', width: '100%', height: '100%', overflow: 'hidden' }}>
        {(!chatOpen || chatMode !== 'fullscreen') && (
          <div style={{ flex: 1, minWidth: 0, overflowY: 'auto' }}>
            {children}
          </div>
        )}
        {shouldRenderChat && (
          <Sheet open={chatOpen} side="end" spacingInset="none" type="push" onClose={handleCloseChat}>
            <Column height="100%" width={chatMode === 'fullscreen' ? '100vw' : CHAT_PANEL_WIDTH_PX}>
              <ChatWidget
                assistantVariant={dataAssistantAvailable ? 'data' : 'campaign'}
                context={chatContext}
                entity={chatEntity}
                initialSessionId={initialSessionId}
                mode={chatMode}
                open={chatOpen}
                onClose={handleCloseChat}
                onModeChange={setChatMode}
                onNewSession={handleNewSession}
                onResume={handleResume}
              />
            </Column>
          </Sheet>
        )}
        <Sheet open={isOpen} side="end" spacingInset="none" type="push" onClose={closeSheet}>
          <Column height="100%" width={400}>
            {sheetContent}
          </Column>
        </Sheet>
      </div>
      {assistantAvailable && !chatOpen && (
        <AgentFab onClick={handleOpenAssistant} />
      )}
    </ChatPanelSessionRegistrationProvider>
  );
};
